import React, { useMemo, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useTable, Column, usePagination } from "react-table";
import { useForm, FormProvider } from "react-hook-form";
import moment from "moment";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

import usePermissions from "hooks/usePermissions";
import errorToast from "helpers/errorToast";
import { ReactComponent as CircleCross } from "assets/images/icons/CircleCross.svg";
import Button from "components/atoms/Button/Button";
import CardNew from "components/molecules/Card/CardNew";
import DataTable from "components/organisms/DataTable/DataTable";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import Input from "components/atoms/Input/Input";
import ScacList from "./MBLScacList";
import SearchSelect from "../components/Common/SearchSelect";

const { REACT_APP_CRUD_MBL } = import.meta.env;

function MBLShipment() {
  const intl = useIntl();
  const methods = useForm({});
  const { permissions } = usePermissions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { handleSubmit, watch } = methods;
  const type = watch("type");
  const queryPage = +(searchParams.get("page") || 0);
  const { company_id } = JSON.parse(permissions as string) as { company_id: number };
  const apiUrl = `https://crudcrud.com/api/${REACT_APP_CRUD_MBL}/${company_id}`;

  const [data, setData] = useState<any>([]);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    instance.gotoPage(queryPage ? queryPage - 1 : 0);
  }, [queryPage]);

  const generatePayload = (type: string, number: string, carrier: string) => {
    // Get the current date and time as the "created" field
    const created = new Date().toISOString();
    const status = "Pending";

    // Create the mock payload
    const payload = {
      payment_location: "Location1",
      created,
      type,
      number,
      status,
      ...((type === "MBL" || type === "Booking") && { carrier }),
    };
    return payload;
  };

  const fetchData = () => {
    setIsFetching(true);
    axios
      .get(apiUrl)
      .then((response) => {
        response.data.reverse();
        setData(response.data);
      })
      .catch((error) => errorToast(error))
      .finally(() => setIsFetching(false));
  };

  const redirectToShipment = (shipmentNumber: string) => {
    if (!shipmentNumber) return;
    navigate(`/shipment/${shipmentNumber}/tracking`);
  };

  const deleteShipment = (id: string) => {
    axios
      .delete(`${apiUrl}/${id}`)
      .then(() => fetchData())
      .catch((error) => errorToast(error));
  };

  // Create a postrequest to the API
  const onSubmit = async (data: any) => {
    setIsSubmitting(true);
    const payload = generatePayload(data.type, data.number, data.carriers);
    axios
      .post(apiUrl, payload)
      .then(() => {
        fetchData();
        methods.reset({ type: "", number: "" });
      })
      .catch((error) => errorToast("Error creating data:", error))
      .finally(() => setIsSubmitting(false));
  };

  const columns = useMemo<Column[]>(
    () => [
      {
        Header: (
          <span className="pl-9 ">
            <FormattedMessage id="general.created" defaultMessage="Created" />
          </span>
        ),
        accessor: "created",
        Cell: ({ value }: { value: Date }) => (
          <p className="pl-9 pt-8 pb-8 mb-0">
            <div className="d-flex align-items-center">
              <p className="font-weight-bold mb-0">{moment(value, "YYYY-MM-DD HH:mm:ss").format("DD MMM YY") || "-"}</p>
              <p className="mb-0 ml-1">{moment(value, "YYYY-MM-DD HH:mm:ss").format("HH:mm UTC") || "-"}</p>
            </div>
          </p>
        ),
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="general.type" defaultMessage="Type" />
          </span>
        ),
        accessor: "type",
        Cell: ({ value }: { value: string }) => <p className="pt-8 pb-8 mb-0">{value}</p>,
      },
      {
        Header: (
          <span className="mb-0 ">
            <FormattedMessage id="general.number" defaultMessage="Number" />
          </span>
        ),
        accessor: "number",
        Cell: ({ value }: { value: string }) => <div className="pt-8 pb-8">{value}</div>,
      },
      {
        Header: (
          <span className="mb-0 ">
            <FormattedMessage id="general.carrier" defaultMessage="Carrier" />
          </span>
        ),
        accessor: "carrier",
        Cell: ({ value }: { value: string }) => <div className="pt-8 pb-8">{value}</div>,
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="general.shipment" defaultMessage="Shipment" />
          </span>
        ),
        accessor: "shipment_id",
        Cell: ({ value }: { value: string }) => (
          <div className="pt-8 pb-8 font-weight-bold" onClick={() => redirectToShipment(value)}>
            {value}
          </div>
        ),
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="general.status" defaultMessage="Status" />
          </span>
        ),
        accessor: "status",
        Cell: ({ value }: { value: string }) => <div className={`pt-8 pb-8 `}>{value}</div>,
      },
      {
        Header: <span className="mb-0 text-right ">POL</span>,
        accessor: "pol",
        Cell: ({ value }: { value: string }) => <div className={`pt-8 pb-8 `}>{value}</div>,
      },
      {
        Header: <span className="mb-0 text-right ">POD</span>,
        accessor: "pod",
        Cell: ({ value }: { value: string }) => <div className={`pt-8 pb-8 `}>{value}</div>,
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="general.containers" defaultMessage="Containers" />
          </span>
        ),
        accessor: "containers",
        Cell: ({ value }: { value: string[] | undefined }) => (
          <div className="pt-8 pb-8">{value ? value.join(", ") : ""}</div>
        ),
      },
      {
        Header: <span className="mb-0 text-right" />,
        accessor: "_id",
        actions: true,
        Cell: ({ value }: { value: string }) => (
          <div>
            <CircleCross stroke="black" onClick={() => deleteShipment(value)} />
          </div>
        ),
      },
    ],
    [],
  );

  const instance = useTable(
    {
      initialState: {
        pageSize: 10,
        pageIndex: queryPage ? queryPage - 1 : 0,
      },
      columns,
      data,
    },
    usePagination,
  );

  return (
    <CardNew className="shadow-none">
      <div className="d-flex align-items-center mb-4">
        <h3>
          <FormattedMessage id="tableHeaders.MBLNumber" defaultMessage="MBL Numbers" />
        </h3>
      </div>
      <DataTable
        tableInstance={instance}
        isLoading={isFetching}
        draggable={false}
        withoutBody
        autoPagination
        totalData={data.length}
        // headerRight={
        //   <div className="d-flex flex-column align-items-center">
        //     <Button>Import</Button>
        //     <span className="text-decoration-underline mt-2">Download template</span>
        //   </div>
        // }
        headerLeft={
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="d-flex">
                <div className="mr-3">
                  <Dropdown
                    options={[
                      { value: "MBL", label: "MBL Number" },
                      { value: "Container", label: "Container Number" },
                      { value: "Booking", label: "Booking Number" },
                    ]}
                    placeholder="Select"
                    useHookForm
                    name="type"
                    required
                  />
                </div>
                {(type === "MBL" || type === "Booking") && (
                  <div className="mr-3">
                    <SearchSelect
                      options={ScacList}
                      required
                      name="carriers"
                      placeholder={intl.formatMessage({
                        id: "general.pleaseSelect",
                        defaultMessage: "Please select",
                      })}
                    />
                  </div>
                )}
                <div>
                  <Input
                    required
                    className="rounded-input input-width-md mr-3"
                    placeholder={intl.formatMessage({
                      id: "general.addNumber",
                      defaultMessage: "Add number",
                    })}
                    name="number"
                  />
                </div>
                <div>
                  <Button isLoading={isSubmitting} type="submit">
                    <FormattedMessage id="general.add" defaultMessage="Add" />
                  </Button>
                </div>
              </div>
            </form>
          </FormProvider>
        }
      />
    </CardNew>
  );
}

export default MBLShipment;
